import { getCampaignsByIds } from '@/apis/campaigns';
import { EventRecord, getEventsByIds } from '@/apis/events';
import { getEventTypes } from '@/apis/eventTypes';
import { isTemplatePlaceholder, TemplatePlaceholder } from '@/types';
import { EventAttendanceFilter } from '@/types/searchFilterTypes';
import {
  createChipLabelText,
  createChipTooltip,
  formatTemplateCapableDateForFilterValue,
} from '@/util';
import { PARTICIPANT_STATUSES_WITH_IDS } from '@/constants/filterCriteria/eventParticipants';
import { migrateRedundantEventTypeIds } from '@pages/SavedSearchesPage/utils';
import { EventAttendanceWidgetValue } from '@/pages/AdvancedSearchListCreatorPage/customWidgets/EventAttendanceWidget';
import { FilterMapper, MappingError, ValidationContext } from './filterMapperTypes';

const createDateChips = (mailings: EventRecord[]) => {
  return mailings.map(({ id, title, startDate = '' }) => {
    const chipTooltipText = createChipTooltip(title, startDate);
    const chipLabel = createChipLabelText(title, startDate);
    return {
      id: id,
      chipLabel: chipLabel,
      label: chipTooltipText,
      tooltipContent: chipTooltipText,
    };
  });
};

const checkArrayOrPlaceholderCriterion = (item: TemplatePlaceholder | unknown[]) => {
  return !isTemplatePlaceholder(item) && item.length === 0;
};

export const EventAttendanceMapper: FilterMapper = {
  checkIsIncomplete({ value }) {
    const {
      statuses,
      eventDateRange,
      eventTypes,
      events,
      eventCampaigns,
    }: EventAttendanceWidgetValue = value[0];

    const isNoCriteriaSelected =
      checkArrayOrPlaceholderCriterion(statuses) &&
      checkArrayOrPlaceholderCriterion(events) &&
      checkArrayOrPlaceholderCriterion(eventTypes) &&
      checkArrayOrPlaceholderCriterion(eventCampaigns) &&
      !eventDateRange.endDate &&
      !eventDateRange.startDate;

    const isStartDateAfterEndDate =
      !!eventDateRange.startDate &&
      !!eventDateRange.endDate &&
      eventDateRange.startDate > eventDateRange.endDate;

    return isNoCriteriaSelected || isStartDateAfterEndDate;
  },

  toFilter({ value }): EventAttendanceFilter {
    if (!value) throw new Error('Missing value');
    // Why an array of length 1? Thats just the way it is the way we use it
    if (value.length !== 1) throw new Error('Invalid value. Expected array of length 1');
    const filterValue: EventAttendanceWidgetValue = value[0];
    const statusIds = isTemplatePlaceholder(filterValue.statuses)
      ? filterValue.statuses
      : filterValue.statuses.map(({ label }) => label);

    const eventIds = isTemplatePlaceholder(filterValue.events)
      ? filterValue.events
      : filterValue.events && filterValue.events.length > 0
        ? filterValue.events.map(({ id }) => id)
        : [];

    const eventCampaignIds = isTemplatePlaceholder(filterValue.eventCampaigns)
      ? filterValue.eventCampaigns
      : filterValue.eventCampaigns && filterValue.eventCampaigns.length > 0
        ? filterValue.eventCampaigns.map(({ id }) => id)
        : [];

    const eventTypeIds = isTemplatePlaceholder(filterValue.eventTypes)
      ? filterValue.eventTypes
      : filterValue.eventTypes
        ? filterValue.eventTypes.map(({ id }) => id)
        : [];

    return {
      name: 'eventAttendance',
      value: {
        statusIds,
        eventIds,
        eventTypeIds,
        eventDateRange: {
          startDate: formatTemplateCapableDateForFilterValue(
            filterValue.eventDateRange.startDate,
            isTemplatePlaceholder,
            undefined
          ),
          endDate: formatTemplateCapableDateForFilterValue(
            filterValue.eventDateRange.endDate,
            isTemplatePlaceholder,
            undefined
          ),
        },
        eventCampaignIds,
      },
    };
  },

  async fromFilter(filter: EventAttendanceFilter, { token, domainCode }: ValidationContext) {
    const allEventTypes = await getEventTypes(token);
    const { eventDateRange, eventIds, eventTypeIds, statusIds, eventCampaignIds } = filter.value;

    const events = isTemplatePlaceholder(eventIds)
      ? eventIds
      : await getEventsByIds(token, eventIds, domainCode);

    const cleanEventTypeIds = isTemplatePlaceholder(eventTypeIds)
      ? eventTypeIds
      : migrateRedundantEventTypeIds(eventTypeIds);

    const eventCampaigns = isTemplatePlaceholder(eventCampaignIds)
      ? eventCampaignIds
      : eventCampaignIds
        ? await getCampaignsByIds(token, eventCampaignIds)
        : [];

    const resolvedEventTypes = isTemplatePlaceholder(cleanEventTypeIds)
      ? []
      : allEventTypes.filter((eventType) => cleanEventTypeIds?.includes(eventType.id));
    const resolvedEvents = isTemplatePlaceholder(events) ? [] : events;
    const resolvedCampaignsCount = isTemplatePlaceholder(eventCampaigns) ? 0 : eventCampaigns.length;

    const errors: MappingError[] = [];
    if (
      !isTemplatePlaceholder(cleanEventTypeIds) &&
      cleanEventTypeIds.length > 0 &&
      resolvedEventTypes.length < cleanEventTypeIds.length
    ) {
      errors.push({ field: 'eventTypes', message: 'Missing event types', level: 'warning' });
    }
    if (
      !isTemplatePlaceholder(eventCampaignIds) &&
      eventCampaignIds.length > 0 &&
      resolvedCampaignsCount < eventCampaignIds.length
    ) {
      errors.push({ field: 'eventCampaigns', message: 'Missing campaigns', level: 'warning' });
    }
    if (
      !isTemplatePlaceholder(eventIds) &&
      eventIds.length > 0 &&
      resolvedEvents.length < eventIds.length
    ) {
      errors.push({ field: 'events', message: 'Missing events', level: 'warning' });
    }

    const value: EventAttendanceWidgetValue = {
      statuses: isTemplatePlaceholder(statusIds)
        ? statusIds
        : PARTICIPANT_STATUSES_WITH_IDS.filter((status) => statusIds.includes(status.label)),
      eventTypes: isTemplatePlaceholder(cleanEventTypeIds)
        ? (eventTypeIds as TemplatePlaceholder)
        : resolvedEventTypes,
      eventDateRange: {
        startDate: isTemplatePlaceholder(eventDateRange?.startDate)
          ? eventDateRange.startDate
          : eventDateRange.startDate
            ? new Date(eventDateRange.startDate)
            : null,
        endDate: isTemplatePlaceholder(eventDateRange?.endDate)
          ? eventDateRange.endDate
          : eventDateRange.endDate
            ? new Date(eventDateRange.endDate)
            : null,
      },
      events: isTemplatePlaceholder(events) ? events : createDateChips(events),
      eventCampaigns: isTemplatePlaceholder(eventCampaigns)
        ? eventCampaigns
        : eventCampaigns.map((x) => {
            return { id: x.id, label: x.title };
          }),
      errors,
    };

    return {
      field: filter.name,
      operator: 'any in',
      value: [value],
    };
  },
};
